import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import contractService from '../../services/contractService';
import countryService from '../../services/countryService';
import { useCountry } from '../../hooks/useCountry';
import authService from '../../services/authService';
import { Button, Spinner, Alert, Badge, Input } from '../ui';

const ContractDownload = () => {
  const { contractId } = useParams();
  const { countryCode } = useCountry();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const jurisdiction = countryCode || 'usa';
  const [countryData, setCountryData] = useState(countryService.getCountryFromCache(jurisdiction));
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadSuccess, setDownloadSuccess] = useState(false);

  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(authService.isAuthenticated());
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [accountForm, setAccountForm] = useState({
    email: '',
    password: '',
    password2: '',
  });
  const [accountError, setAccountError] = useState('');
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountCreated, setAccountCreated] = useState(false);

  useEffect(() => {
    const fetchCountryData = async () => {
      const data = await countryService.getCountryByCode(jurisdiction);
      if (data) {
        setCountryData(data);
      }
    };
    fetchCountryData();
  }, [jurisdiction]);

  useEffect(() => {
    loadContract();
  }, [contractId]);

  const getCurrencySymbol = (currency) => {
    const symbols = { EUR: '€', GBP: '£', USD: '$', CAD: 'CA$', AUD: 'A$' };
    return symbols[currency] || '$';
  };

  const getContractTypeLabel = () => {
    const labels = {
      usa: 'Residential Lease Agreement',
      france: "Bail d'Habitation",
      germany: 'Mietvertrag (Wohnraum)',
      spain: 'Contrato de Arrendamiento',
      italy: 'Contratto di Locazione',
      uk: 'Assured Shorthold Tenancy',
      canada: 'Residential Tenancy Agreement',
      australia: 'Residential Tenancy Agreement',
    };
    return labels[jurisdiction] || 'Residential Lease Agreement';
  };

  const getContractDetails = () => {
    if (!contract) return [];

    const details = [];

    // Property address
    const property = contract.property || {};
    if (property.address_line1 || contract.property_address) {
      details.push({
        label: 'Property',
        value: property.address_line1 || contract.property_address,
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
        ),
      });
    }

    // Landlord
    const landlord = contract.landlord || {};
    if (landlord.full_name || contract.landlord_name) {
      details.push({
        label: 'Landlord',
        value: landlord.full_name || contract.landlord_name,
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        ),
      });
    }

    // Tenant
    const tenant = contract.tenant || {};
    if (tenant.full_name || contract.tenant_name) {
      details.push({
        label: 'Tenant',
        value: tenant.full_name || contract.tenant_name,
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        ),
      });
    }

    // Monthly Rent
    if (contract.monthly_rent) {
      details.push({
        label: 'Monthly Rent',
        value: `${getCurrencySymbol(countryData?.currency)}${contract.monthly_rent}`,
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
      });
    }

    // Lease Start
    if (contract.lease_start_date) {
      details.push({
        label: 'Start Date',
        value: new Date(contract.lease_start_date).toLocaleDateString(),
        icon: (
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        ),
      });
    }

    return details;
  };

  const loadContract = async () => {
    try {
      setLoading(true);
      const data = await contractService.getContract(jurisdiction, contractId);

      // Verify payment status
      if (data.document_status !== 'paid' && data.document_status !== 'finalized') {
        setError('Payment verification failed. Please contact support.');
        setLoading(false);
        return;
      }

      setContract(data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load contract:', error);
      setError('Failed to load contract. Please try again.');
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const downloadUrl = contractService.getDownloadUrl(jurisdiction, contractId);
      window.location.href = downloadUrl;
      setDownloadSuccess(true);
      setTimeout(() => setDownloadSuccess(false), 3000);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setAccountError('');

    // Validate passwords match
    if (accountForm.password !== accountForm.password2) {
      setAccountError('Passwords do not match');
      return;
    }

    // Validate password length
    if (accountForm.password.length < 8) {
      setAccountError('Password must be at least 8 characters');
      return;
    }

    setAccountLoading(true);

    try {
      // Create account with contract association
      await authService.register({
        email: accountForm.email,
        username: accountForm.email.split('@')[0],
        password: accountForm.password,
        password2: accountForm.password2,
        contract_id: contractId,
      });

      // Auto-login
      await authService.login(accountForm.email, accountForm.password);
      setIsAuthenticated(true);
      setAccountCreated(true);
      setShowCreateAccount(false);
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData) {
        const errorMessages = Object.entries(errorData)
          .map(([key, value]) => `${Array.isArray(value) ? value.join(', ') : value}`)
          .join('. ');
        setAccountError(errorMessages);
      } else {
        setAccountError('Failed to create account. Please try again.');
      }
    } finally {
      setAccountLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-emerald-50 flex items-center justify-center">
        <Spinner.Page message="Loading your contract..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-rose-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-soft p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-rose-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Unable to Load Contract</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/')} fullWidth>
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  const contractDetails = getContractDetails();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-emerald-50 pb-24 md:pb-8">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-xl border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/')}
                className="p-2 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-lg md:text-xl font-bold text-gray-900">Download Contract</h1>
                </div>
                <p className="text-xs md:text-sm text-gray-500 hidden sm:block">{getContractTypeLabel()}</p>
              </div>
            </div>
            <Link
              to="/dashboard"
              className="hidden sm:flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Dashboard
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6 md:py-8">
        {/* Success Banner */}
        <div className="bg-white rounded-2xl shadow-soft overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 p-6 md:p-8 text-white text-center">
            {/* Success Animation */}
            <div className="w-16 h-16 md:w-20 md:h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce-slow">
              <svg className="w-8 h-8 md:w-10 md:h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold mb-2">Payment Successful!</h1>
            <p className="text-emerald-100 text-sm md:text-base">
              Your contract is ready to download. Thank you for your purchase.
            </p>
          </div>

          {/* Download Success Message */}
          {downloadSuccess && (
            <div className="bg-emerald-50 border-b border-emerald-100 px-4 py-3 text-center">
              <p className="text-emerald-700 text-sm font-medium flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Download started!
              </p>
            </div>
          )}
        </div>

        {/* Contract Details Card */}
        <div className="bg-white rounded-2xl shadow-soft p-5 md:p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Contract Details</h2>
            <Badge.Status status={contract?.document_status || 'paid'} />
          </div>

          {/* Contract Info Grid */}
          <div className="space-y-0">
            {/* Country & Type - Always show */}
            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <span className="text-gray-600 text-sm">Country</span>
              </div>
              <span className="font-medium text-gray-900 text-sm">{countryData?.name}</span>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <span className="text-gray-600 text-sm">Type</span>
              </div>
              <span className="font-medium text-gray-900 text-sm text-right max-w-[180px]">{getContractTypeLabel()}</span>
            </div>

            {/* Dynamic Contract Details */}
            {contractDetails.map((detail, index) => (
              <div key={index} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 text-gray-600">
                    {detail.icon}
                  </div>
                  <span className="text-gray-600 text-sm">{detail.label}</span>
                </div>
                <span className="font-medium text-gray-900 text-sm text-right max-w-[180px] truncate">{detail.value}</span>
              </div>
            ))}

            {/* Contract ID */}
            <div className="flex items-center justify-between py-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                  </svg>
                </div>
                <span className="text-gray-600 text-sm">Contract ID</span>
              </div>
              <span className="font-mono text-xs bg-gray-100 px-3 py-1.5 rounded-lg text-gray-700">{contractId?.slice(0, 12)}...</span>
            </div>
          </div>
        </div>

        {/* Download Card - Desktop */}
        <div className="hidden md:block bg-white rounded-2xl shadow-soft overflow-hidden mb-6">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Download Your Contract</h2>
            <p className="text-gray-600 text-sm mb-6">
              Your final contract is ready. The document is legally formatted, without watermarks, and ready to print and sign.
            </p>

            <Button
              onClick={handleDownload}
              loading={downloading}
              fullWidth
              size="lg"
              icon={({ className }) => (
                <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              )}
            >
              {downloading ? 'Preparing Download...' : 'Download Final PDF'}
            </Button>

            <p className="text-xs text-gray-500 text-center mt-3">
              You can download this document anytime by returning to this page.
            </p>
          </div>
        </div>

        {/* What's Next Card */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-5 md:p-6 mb-6 border border-blue-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
            What's Next?
          </h3>
          <div className="space-y-3">
            {[
              { step: '1', text: 'Download and save the PDF to your device' },
              { step: '2', text: 'Print the contract (we recommend 2 copies)' },
              { step: '3', text: 'Have all parties review and sign the contract' },
              { step: '4', text: 'Keep a signed copy for your records' },
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {item.step}
                </div>
                <span className="text-gray-700 text-sm">{item.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Create Another Card */}
        <div className="bg-white rounded-2xl shadow-soft p-5 md:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="font-semibold text-gray-900">Need Another Contract?</h3>
              <p className="text-sm text-gray-500 mt-1">Create a new contract for a different property or tenant.</p>
            </div>
            <Button
              variant="secondary"
              onClick={() => navigate('/contracts/create')}
              icon={({ className }) => (
                <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              )}
            >
              New Contract
            </Button>
          </div>
        </div>

        {/* Create Account Card - for guest users */}
        {!isAuthenticated && !accountCreated && (
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-5 md:p-6 mb-6 border border-amber-200">
            {!showCreateAccount ? (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Save This Contract to Your Account</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Create a free account to access your contracts anytime, get legal updates, and manage multiple properties.
                    </p>
                  </div>
                </div>
                <Button
                  onClick={() => setShowCreateAccount(true)}
                  className="!bg-amber-500 hover:!bg-amber-600 flex-shrink-0"
                  icon={({ className }) => (
                    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                  )}
                >
                  Create Account
                </Button>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">Create Your Free Account</h3>
                  <button
                    onClick={() => setShowCreateAccount(false)}
                    className="p-1 rounded-lg hover:bg-amber-100 text-gray-500"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {accountError && (
                  <Alert type="error" className="mb-4" dismissible onDismiss={() => setAccountError('')}>
                    {accountError}
                  </Alert>
                )}

                <form onSubmit={handleCreateAccount} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                    <input
                      type="email"
                      value={accountForm.email}
                      onChange={(e) => setAccountForm({ ...accountForm, email: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      placeholder="your@email.com"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                    <input
                      type="password"
                      value={accountForm.password}
                      onChange={(e) => setAccountForm({ ...accountForm, password: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      placeholder="At least 8 characters"
                      required
                      minLength={8}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                    <input
                      type="password"
                      value={accountForm.password2}
                      onChange={(e) => setAccountForm({ ...accountForm, password2: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      placeholder="Confirm your password"
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    fullWidth
                    loading={accountLoading}
                    className="!bg-amber-500 hover:!bg-amber-600"
                  >
                    Create Account & Save Contract
                  </Button>

                  <p className="text-xs text-gray-500 text-center">
                    By creating an account, you agree to our{' '}
                    <Link to="/terms" className="text-amber-600 hover:underline">Terms of Service</Link>
                    {' '}and{' '}
                    <Link to="/privacy" className="text-amber-600 hover:underline">Privacy Policy</Link>.
                  </p>
                </form>
              </div>
            )}
          </div>
        )}

        {/* Account Created Success */}
        {accountCreated && (
          <div className="bg-emerald-50 rounded-2xl p-5 md:p-6 mb-6 border border-emerald-200">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Account Created Successfully!</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Your contract has been saved to your account. Access it anytime from your{' '}
                  <Link to="/dashboard" className="text-emerald-600 hover:underline font-medium">dashboard</Link>.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Support Card */}
        <div className="bg-white rounded-2xl shadow-soft p-5 md:p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-gray-900">Need Help?</h4>
              <p className="text-sm text-gray-500 mt-1">
                If you have any questions about your contract or need assistance, our support team is here to help.
              </p>
              <div className="flex flex-wrap gap-3 mt-3">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  Contact Support
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
                <Link
                  to="/faq"
                  className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-700 font-medium"
                >
                  View FAQ
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Sticky Download Button */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-40 shadow-lg">
        <Button
          onClick={handleDownload}
          loading={downloading}
          fullWidth
          size="lg"
          icon={({ className }) => (
            <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          )}
        >
          {downloading ? 'Preparing...' : 'Download PDF'}
        </Button>
        <p className="text-xs text-gray-500 text-center mt-2">
          Final document without watermark
        </p>
      </div>

      {/* Add custom animation */}
      <style>{`
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default ContractDownload;
